
import React, { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import { Edit, Trash2, Plus, ArrowLeft } from 'lucide-react'
import TechStackForm from './TechStackForm'
import ContentImageForm from './ContentImageForm'
import ClientForm from './ClientForm'

interface TechStackItem {
  id: string
  category: string
  technologies: string[]
  image_url: string | null
  display_order: number
  is_active: boolean
}

interface Client {
  id: string
  name: string
  industry: string
  logo_url: string | null
  projects: string[]
  tech_stack: string[]
  website_url: string | null
  display_order: number
  is_active: boolean
}

interface ContentImage {
  id: string
  section_name: string
  image_url: string
  alt_text: string | null
  display_order: number
  is_active: boolean
}

const ContentManager = () => {
  const [techStack, setTechStack] = useState<TechStackItem[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [contentImages, setContentImages] = useState<ContentImage[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'tech-stack' | 'clients' | 'content-images'>('clients')
  const [editingTechStack, setEditingTechStack] = useState<TechStackItem | null>(null)
  const [editingClient, setEditingClient] = useState<Client | null>(null)
  const [editingContentImage, setEditingContentImage] = useState<ContentImage | null>(null)
  const [showTechStackForm, setShowTechStackForm] = useState(false)
  const [showClientForm, setShowClientForm] = useState(false)
  const [showContentImageForm, setShowContentImageForm] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [techStackResponse, clientsResponse, contentImagesResponse] = await Promise.all([
        supabase.from('tech_stack').select('*').order('display_order'),
        supabase.from('clients').select('*').order('display_order'),
        supabase.from('content_images').select('*').order('section_name, display_order')
      ])
      
      if (techStackResponse.error) throw techStackResponse.error
      if (clientsResponse.error) throw clientsResponse.error
      if (contentImagesResponse.error) throw contentImagesResponse.error
      
      setTechStack(techStackResponse.data || [])
      setClients(clientsResponse.data || [])
      setContentImages(contentImagesResponse.data || [])
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch content data",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteTechStack = async (id: string) => {
    if (!confirm('Are you sure you want to delete this tech stack item?')) return

    try {
      const { error } = await supabase
        .from('tech_stack')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      
      toast({
        title: "Success",
        description: "Tech stack item deleted successfully",
      })
      
      fetchData()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete tech stack item",
        variant: "destructive",
      })
    }
  }

  const handleDeleteClient = async (id: string) => {
    if (!confirm('Are you sure you want to delete this client?')) return

    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      
      toast({
        title: "Success",
        description: "Client deleted successfully",
      })
      
      fetchData()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete client",
        variant: "destructive",
      })
    }
  }

  const handleDeleteContentImage = async (id: string) => {
    if (!confirm('Are you sure you want to delete this content image?')) return

    try {
      const { error } = await supabase
        .from('content_images')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      
      toast({
        title: "Success",
        description: "Banner image deleted successfully",
      })
      
      fetchData()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete banner image",
        variant: "destructive",
      })
    }
  }

  const handleEditTechStack = (item: TechStackItem) => {
    setEditingTechStack(item)
    setShowTechStackForm(true)
  }

  const handleAddTechStack = () => {
    setEditingTechStack(null)
    setShowTechStackForm(true)
  }

  const handleEditContentImage = (item: ContentImage) => {
    setEditingContentImage(item)
    setShowContentImageForm(true)
  }

  const handleEditClient = (client: Client) => {
    setEditingClient(client)
    setShowClientForm(true)
  }

  const handleAddClient = () => {
    setEditingClient(null)
    setShowClientForm(true)
  }

  const handleAddContentImage = () => {
    setEditingContentImage(null)
    setShowContentImageForm(true)
  }

  const handleTechStackFormSave = () => {
    setShowTechStackForm(false)
    setEditingTechStack(null)
    fetchData()
  }

  const handleTechStackFormCancel = () => {
    setShowTechStackForm(false)
    setEditingTechStack(null)
  }

  const handleClientFormSave = () => {
    setShowClientForm(false)
    setEditingClient(null)
    fetchData()
  }

  const handleClientFormCancel = () => {
    setShowClientForm(false)
    setEditingClient(null)
  }

  const handleContentImageFormSave = () => {
    setShowContentImageForm(false)
    setEditingContentImage(null)
    fetchData()
  }

  const handleContentImageFormCancel = () => {
    setShowContentImageForm(false)
    setEditingContentImage(null)
  }

  if (loading) {
    return <div>Loading content...</div>
  }

  if (showTechStackForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={handleTechStackFormCancel}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tech Stack
          </Button>
          <h2 className="text-2xl font-bold">
            {editingTechStack ? 'Edit Tech Stack' : 'Add New Tech Stack'}
          </h2>
        </div>
        <TechStackForm
          techStackItem={editingTechStack}
          onSave={handleTechStackFormSave}
          onCancel={handleTechStackFormCancel}
        />
      </div>
    )
  }

  if (showClientForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={handleClientFormCancel}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Clients
          </Button>
          <h2 className="text-2xl font-bold">
            {editingClient ? 'Edit Client' : 'Add New Client'}
          </h2>
        </div>
        <ClientForm
          client={editingClient}
          onSave={handleClientFormSave}
          onCancel={handleClientFormCancel}
        />
      </div>
    )
  }

  if (showContentImageForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={handleContentImageFormCancel}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Banner Images
          </Button>
          <h2 className="text-2xl font-bold">
            {editingContentImage ? 'Edit Banner Image' : 'Add New Banner Image'}
          </h2>
        </div>
        <ContentImageForm
          contentImage={editingContentImage}
          onSave={handleContentImageFormSave}
          onCancel={handleContentImageFormCancel}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Manage Content</h2>
        <div className="flex gap-2">
          <Button 
            variant={activeTab === 'clients' ? 'default' : 'outline'}
            onClick={() => setActiveTab('clients')}
          >
            Clients
          </Button>
          <Button 
            variant={activeTab === 'content-images' ? 'default' : 'outline'}
            onClick={() => setActiveTab('content-images')}
          >
            Banner Images
          </Button>
        </div>
      </div>

      {activeTab === 'tech-stack' && (
        <Card>
          <CardHeader>
            <CardTitle>Tech Stack</CardTitle>
            <CardDescription>Manage technology categories and tools</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-end mb-4">
              <Button onClick={handleAddTechStack}>
                <Plus className="w-4 h-4 mr-2" />
                Add Tech Category
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Technologies</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {techStack.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.category}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {item.technologies.slice(0, 3).map((tech, index) => (
                          <span key={index} className="px-2 py-1 bg-gray-100 rounded text-xs">
                            {tech}
                          </span>
                        ))}
                        {item.technologies.length > 3 && (
                          <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                            +{item.technologies.length - 3} more
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{item.display_order}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        item.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {item.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditTechStack(item)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => handleDeleteTechStack(item.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {activeTab === 'clients' && (
        <Card>
          <CardHeader>
            <CardTitle>Clients</CardTitle>
            <CardDescription>Manage client information</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-end mb-4">
              <Button onClick={handleAddClient}>
                <Plus className="w-4 h-4 mr-2" />
                Add Client
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Projects</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.id}>
                    <TableCell className="font-medium">{client.name}</TableCell>
                    <TableCell>{client.industry}</TableCell>
                    <TableCell>{client.projects.length} project(s)</TableCell>
                    <TableCell>{client.display_order}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        client.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {client.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditClient(client)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => handleDeleteClient(client.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {activeTab === 'content-images' && (
        <Card>
          <CardHeader>
            <CardTitle>Banner Images</CardTitle>
            <CardDescription>Manage images for different sections of the website</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-end mb-4">
              <Button onClick={handleAddContentImage}>
                <Plus className="w-4 h-4 mr-2" />
                Add Banner Image
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Section</TableHead>
                  <TableHead>Image</TableHead>
                  <TableHead>Alt Text</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contentImages.map((image) => (
                  <TableRow key={image.id}>
                    <TableCell className="font-medium capitalize">{image.section_name.replace('-', ' ')}</TableCell>
                    <TableCell>
                      <img 
                        src={image.image_url} 
                        alt={image.alt_text || 'Banner Image'} 
                        className="w-16 h-16 object-cover rounded"
                      />
                    </TableCell>
                    <TableCell>{image.alt_text || 'No alt text'}</TableCell>
                    <TableCell>{image.display_order}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        image.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {image.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditContentImage(image)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => handleDeleteContentImage(image.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default ContentManager
